import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { TreatmentPicker } from "@/components/treatment-picker";
import { trpc } from "@/trpc/trpc";
import { toast } from "sonner";
import { X } from "lucide-react";

const schema = z.object({
  name: z.string().min(1, "Nazwa jest wymagana"),
  sortOrder: z.coerce.number().int(),
  isActive: z.boolean(),
  treatmentIds: z.array(z.string().uuid()),
});

type FormData = z.infer<typeof schema>;

export type TagForEdit = {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  treatmentIds: string[];
};

export function TagDialog({
  open,
  onOpenChange,
  tag,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tag?: TagForEdit | null;
}) {
  const utils = trpc.useUtils();
  const isEdit = !!tag;

  const { data: treatmentList } = trpc.treatment.list.useQuery(
    { page: 1, limit: 100 },
    { enabled: open },
  );

  const createMutation = trpc.tag.create.useMutation({
    onSuccess: () => {
      utils.tag.list.invalidate();
      utils.treatment.list.invalidate();
      toast.success("Tag dodany");
      onOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = trpc.tag.update.useMutation({
    onSuccess: () => {
      utils.tag.list.invalidate();
      utils.treatment.list.invalidate();
      toast.success("Tag zaktualizowany");
      onOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      sortOrder: 0,
      isActive: true,
      treatmentIds: [],
    },
  });

  useEffect(() => {
    if (!open) return;
    if (tag) {
      form.reset({
        name: tag.name,
        sortOrder: tag.sortOrder,
        isActive: tag.isActive,
        treatmentIds: tag.treatmentIds,
      });
    } else {
      form.reset({
        name: "",
        sortOrder: 0,
        isActive: true,
        treatmentIds: [],
      });
    }
  }, [open, tag, form]);

  const watchTreatmentIds = form.watch("treatmentIds");
  const watchActive = form.watch("isActive");

  const onSubmit = form.handleSubmit((data) => {
    const payload = {
      name: data.name,
      sortOrder: data.sortOrder,
      isActive: data.isActive,
      treatmentIds: data.treatmentIds,
    };

    if (isEdit && tag) {
      updateMutation.mutate({ id: tag.id, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const toggleTreatment = (id: string) => {
    const current = form.getValues("treatmentIds");
    if (current.includes(id)) {
      form.setValue(
        "treatmentIds",
        current.filter((t) => t !== id),
        { shouldValidate: true },
      );
    } else {
      form.setValue("treatmentIds", [...current, id], { shouldValidate: true });
    }
  };

  const treatmentMap = new Map(
    (treatmentList?.items ?? []).map((t) => [t.id, t]),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edytuj tag" : "Nowy tag"}</DialogTitle>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit}>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tag-name">Nazwa</Label>
              <Input
                id="tag-name"
                placeholder="np. Paznokcie"
                {...form.register("name")}
              />
              {form.formState.errors.name && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tag-sort">Kolejność</Label>
                <Input
                  id="tag-sort"
                  type="number"
                  {...form.register("sortOrder")}
                />
                <p className="text-xs text-muted-foreground">
                  Mniejsza = wcześniej na liście
                </p>
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch
                  id="tag-active"
                  checked={watchActive}
                  onCheckedChange={(v) => form.setValue("isActive", v)}
                />
                <Label htmlFor="tag-active">
                  {watchActive ? "Aktywny" : "Nieaktywny"}
                </Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Wybrane zabiegi ({watchTreatmentIds.length})</Label>
              <div className="flex flex-wrap gap-2 min-h-9">
                {watchTreatmentIds.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Brak wybranych zabiegów
                  </p>
                ) : (
                  watchTreatmentIds.map((id) => {
                    const t = treatmentMap.get(id);
                    return (
                      <Badge
                        key={id}
                        variant="secondary"
                        className="cursor-pointer"
                        onClick={() => toggleTreatment(id)}
                      >
                        {t?.name ?? "(usunięty zabieg)"}
                        <X className="ml-1 size-3" />
                      </Badge>
                    );
                  })
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Dostępne zabiegi</Label>
              <TreatmentPicker
                mode="multi"
                selectedIds={watchTreatmentIds}
                onPick={toggleTreatment}
                enabled={open}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Anuluj
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? "Zapisywanie..."
                : isEdit
                  ? "Zapisz zmiany"
                  : "Dodaj tag"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
